import { useEffect, useState } from "react";

/** Idle time after which automatic network refreshes pause. */
export const AUTOMATIC_REFRESH_INACTIVITY_MILLISECONDS = 5 * 60 * 1_000;

/**
 * Tracks whether automatic network refreshes should run.
 *
 * Refreshes run only while the document is visible and the user has interacted
 * with it recently. Pointer, keyboard, touch, focus, and visibility activity
 * resume refreshes immediately.
 *
 * @returns Whether automatic network refreshes should currently run.
 */
export function useAutomaticRefreshActivity(): boolean {
  const [automaticRefreshActive, setAutomaticRefreshActive] = useState(
    document.visibilityState === "visible",
  );

  useEffect(() => {
    let inactivityTimer: number | undefined;

    /**
     * Resets the inactivity window or pauses immediately when hidden.
     *
     * @returns Nothing.
     */
    const recordActivity = (): void => {
      if (inactivityTimer !== undefined) {
        window.clearTimeout(inactivityTimer);
        inactivityTimer = undefined;
      }
      if (document.visibilityState !== "visible") {
        setAutomaticRefreshActive(false);
        return;
      }
      setAutomaticRefreshActive(true);
      inactivityTimer = window.setTimeout(() => {
        inactivityTimer = undefined;
        setAutomaticRefreshActive(false);
      }, AUTOMATIC_REFRESH_INACTIVITY_MILLISECONDS);
    };

    recordActivity();
    document.addEventListener("pointerdown", recordActivity, { passive: true });
    document.addEventListener("keydown", recordActivity);
    document.addEventListener("touchstart", recordActivity, { passive: true });
    document.addEventListener("visibilitychange", recordActivity);
    window.addEventListener("focus", recordActivity);

    return () => {
      if (inactivityTimer !== undefined) {
        window.clearTimeout(inactivityTimer);
      }
      document.removeEventListener("pointerdown", recordActivity);
      document.removeEventListener("keydown", recordActivity);
      document.removeEventListener("touchstart", recordActivity);
      document.removeEventListener("visibilitychange", recordActivity);
      window.removeEventListener("focus", recordActivity);
    };
  }, []);

  return automaticRefreshActive;
}
